import React from 'react';
import { Sunset, Sunrise, Sun, Clock, Check, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { timeSlots } from '../data/mock';

const iconMap = {
  Sunset: Sunset,
  Sunrise: Sunrise,
  Sun: Sun,
  Clock: Clock
};

export const TimeSlots = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="time-slots" className="py-24 bg-gradient-to-b from-white to-blue-50/30 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-100/40 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
            Nos Créneaux Horaires
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Choisissez votre
            <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent"> moment idéal</span>
          </h2>
          <p className="text-lg text-gray-600">
            De la fraîcheur matinale au coucher de soleil romantique, profitez de la Méditerranée à votre rythme
          </p>
        </div>

        {/* Time Slots Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {timeSlots.map((slot) => {
            const IconComponent = iconMap[slot.icon];
            return (
              <Card 
                key={slot.id}
                className={`group hover:shadow-2xl transition-all duration-300 border-2 overflow-hidden ${
                  slot.highlight 
                    ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-pink-50 hover:scale-105' 
                    : 'border-gray-200 hover:border-blue-500 hover:scale-105'
                }`}
              >
                {slot.highlight && (
                  <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-center py-2 px-4 flex items-center justify-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-semibold">COUP DE CŒUR</span>
                  </div>
                )}
                
                <CardHeader>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all group-hover:scale-110 ${
                    slot.highlight 
                      ? 'bg-gradient-to-br from-orange-500 to-pink-500' 
                      : 'bg-gradient-to-br from-blue-500 to-blue-700'
                  }`}>
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-2xl">{slot.name}</CardTitle>
                  <CardDescription className="text-lg font-semibold text-gray-700">
                    {slot.time}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <p className="text-gray-600">{slot.description}</p>
                  
                  <div className="space-y-2">
                    {slot.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {slot.note && (
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                      <p className="text-xs text-blue-800 font-medium">{slot.note}</p>
                    </div>
                  )}
                </CardContent>

                <CardFooter className="flex flex-col gap-3">
                  <div className="text-center w-full">
                    {slot.priceSpring ? (
                      <div>
                        <div className="text-3xl font-bold text-gray-900">
                          {slot.priceSpring}€ - {slot.priceSummer}€
                        </div>
                        <div className="text-sm text-gray-500">{slot.duration}</div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-4xl font-bold text-gray-900">{slot.price}€</div>
                        <div className="text-sm text-gray-500">{slot.duration}</div>
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={scrollToContact}
                    className={`w-full transition-all ${
                      slot.highlight
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600'
                        : 'bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900'
                    }`}
                  >
                    Réserver ce créneau
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 bg-white shadow-lg rounded-full px-6 py-3 border border-gray-200">
            <span className="text-gray-700">💳 Paiement sécurisé</span>
            <span className="text-gray-300">•</span>
            <span className="text-gray-700">📞 Service client réactif</span>
            <span className="text-gray-300">•</span>
            <span className="text-gray-700">✨ Satisfaction garantie</span>
          </div>
        </div>
      </div>
    </section>
  );
};
